import React, { useState, useMemo } from 'react';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer
} from 'recharts';
import { 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  Box,
  Typography
} from '@mui/material';

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const currentEventValue = payload.find(p => p.dataKey === 'currentEvent')?.value || 0;
    const comparisonEventValue = payload.find(p => p.dataKey === 'comparisonEvent')?.value || 0;
    const currentEventName = payload.find(p => p.dataKey === 'currentEvent')?.payload?.currentEventName || 'Current Event';
    const comparisonEventName = payload.find(p => p.dataKey === 'comparisonEvent')?.payload?.comparisonEventName || 'Comparison Event';
    
    return (
      <div style={{
        backgroundColor: 'rgba(255, 255, 255, 0.98)',
        border: '1px solid rgba(0,0,0,0.1)',
        borderRadius: '12px',
        padding: '16px',
        boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
        backdropFilter: 'blur(10px)',
        minWidth: '180px'
      }}>
        <div style={{ 
          fontWeight: 600, 
          color: '#1a1a1a', 
          marginBottom: '12px',
          fontSize: '14px'
        }}>
          {label} Insurance Leads
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '12px',
              height: '12px',
              backgroundColor: '#4ade80',
              borderRadius: '50%',
              boxShadow: '0 0 0 2px rgba(74, 222, 128, 0.2)'
            }} />
            <span style={{ fontWeight: 500, fontSize: '13px' }}>
              {currentEventName}: <strong style={{ color: '#16a34a' }}>{currentEventValue}</strong>
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '12px',
              height: '12px',
              backgroundColor: '#8b5cf6',
              borderRadius: '50%',
              boxShadow: '0 0 0 2px rgba(139, 92, 246, 0.2)'
            }} />
            <span style={{ fontWeight: 500, fontSize: '13px' }}>
              {comparisonEventName}: <strong style={{ color: '#7c3aed' }}>{comparisonEventValue}</strong>
            </span>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

export default function EventLineChart({ height = 360, selectedEvent, events = [], submissions = [] }) {
  // State for comparison event selection
  const [comparisonEventId, setComparisonEventId] = useState('');

  // Process submissions data by event and insurance type
  const chartData = useMemo(() => {
    if (!selectedEvent || !events.length) {
      // Fallback to demo data structure
      return [
        { type: 'Auto', currentEvent: 0, comparisonEvent: 0, currentEventName: 'Current Event', comparisonEventName: 'Comparison Event' },
        { type: 'Home', currentEvent: 0, comparisonEvent: 0, currentEventName: 'Current Event', comparisonEventName: 'Comparison Event' },
        { type: 'Business', currentEvent: 0, comparisonEvent: 0, currentEventName: 'Current Event', comparisonEventName: 'Comparison Event' },
        { type: 'Life', currentEvent: 0, comparisonEvent: 0, currentEventName: 'Current Event', comparisonEventName: 'Comparison Event' }
      ];
    }

    const comparisonEvent = events.find(e => (e._id || e.id) === comparisonEventId);
    
    // Group submissions by event and insurance type
    const getInsuranceDataForEvent = (eventId) => {
  if (!submissions || !Array.isArray(submissions)) return {};

  const eventSubmissions = submissions.filter(submission => {
    // ✅ Safely check eventId before accessing _id
    if (!submission.eventId) return false;
    return (
      submission.eventId._id === eventId ||
      submission.eventId.id === eventId
    );
  });

  const insuranceData = {};

  eventSubmissions.forEach(submission => {
    if (submission.insuranceTypes && Array.isArray(submission.insuranceTypes)) {
      submission.insuranceTypes.forEach(insurance => {
        const type = typeof insurance === 'string' ? insurance : insurance.type;
        if (type) {
          const capitalizedType = type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
          insuranceData[capitalizedType] = (insuranceData[capitalizedType] || 0) + 1;
        }
      });
    }
  });

  return insuranceData;
};

    const currentEventData = getInsuranceDataForEvent(selectedEvent._id || selectedEvent.id);
    const comparisonEventData = comparisonEvent ? getInsuranceDataForEvent(comparisonEvent._id || comparisonEvent.id) : {};
    console.log("test", currentEventData, comparisonEventData);
    // Create chart data for standard insurance types
    const types = ['Auto', 'Home', 'Business', 'Life'];
    return types.map(type => ({
      type,
      currentEvent: currentEventData[type] || 0,
      comparisonEvent: comparisonEventData[type] || 0,
      currentEventName: selectedEvent.name,
      comparisonEventName: comparisonEvent ? comparisonEvent.name : 'No Comparison'
    }));
  }, [selectedEvent, comparisonEventId, events, submissions]);

  // Get available events for comparison (exclude current event)
  const availableComparisonEvents = useMemo(() => {
    return events.filter(event => (event._id || event.id) !== (selectedEvent?._id || selectedEvent?.id));
  }, [events, selectedEvent]);

  // Auto-select first available comparison event
  React.useEffect(() => {
    if (availableComparisonEvents.length > 0 && !comparisonEventId) {
      setComparisonEventId(availableComparisonEvents[0]._id || availableComparisonEvents[0].id);
    }
  }, [availableComparisonEvents, comparisonEventId]);

  const getTotalLeads = () => {
    return chartData.reduce((sum, item) => sum + item.currentEvent + item.comparisonEvent, 0);
  };

  const getTopPerformer = () => {
    const maxItem = chartData.reduce((max, item) => 
      (item.currentEvent + item.comparisonEvent) > (max.currentEvent + max.comparisonEvent) ? item : max,
      chartData[0] || { type: 'Auto' }
    );
    return maxItem.type;
  };

  const getCurrentEventTotal = () => {
    console.log(chartData);
    return chartData.reduce((sum, item) => sum + item.currentEvent, 0);
  };

  const getComparisonEventTotal = () => {
    return chartData.reduce((sum, item) => sum + item.comparisonEvent, 0);
  };

  return (
    <div style={{ 
      height: '100%',
      borderRadius: '12px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
      border: '1px solid rgba(0,0,0,0.06)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Top accent border */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        background: 'linear-gradient(90deg, #4ade80, #8b5cf6)'
      }} />
      
      <div style={{ height: '100%', padding: '16px' }}>
        {/* Header with Comparison Dropdown */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{ 
                backgroundColor: '#4ade80', 
                width: '48px', 
                height: '48px',
                borderRadius: '50%',
                boxShadow: '0 4px 12px rgba(74, 222, 128, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '20px'
              }}>
                📊
              </div>
              <div>
                <h2 style={{ 
                  color: '#1a1a1a', 
                  marginBottom: '4px',
                  fontSize: '20px',
                  fontWeight: 700,
                  margin: 0
                }}>
                  Event Leads Comparison
                </h2>
                <p style={{ 
                  color: '#6b7280', 
                  fontWeight: 500,
                  margin: 0,
                  fontSize: '14px'
                }}>
                  Compare lead generation across insurance types
                </p>
              </div>
            </div>

            {/* Comparison Event Dropdown */}
            <Box sx={{ minWidth: 200 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Compare with</InputLabel>
                <Select
                  value={comparisonEventId}
                  label="Compare with"
                  onChange={(e) => setComparisonEventId(e.target.value)}
                  sx={{
                    borderRadius: 2,
                    '& .MuiSelect-select': {
                      fontWeight: 600
                    }
                  }}
                >
                  <MenuItem value="">
                    <em>No Comparison</em>
                  </MenuItem>
                  {availableComparisonEvents.map(event => (
                    <MenuItem key={event._id || event.id} value={event._id || event.id}>
                      {event.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </div>
          
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <span style={{
              backgroundColor: '#dcfce7',
              color: '#166534',
              fontWeight: 600,
              padding: '6px 12px',
              borderRadius: '16px',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              📈 {selectedEvent?.name || 'Current'}: {getCurrentEventTotal()} leads
            </span>
            {comparisonEventId && (
              <span style={{
                backgroundColor: '#ede9fe',
                color: '#581c87',
                fontWeight: 600,
                padding: '6px 12px',
                borderRadius: '16px',
                fontSize: '13px'
              }}>
                📊 {chartData[0]?.comparisonEventName}: {getComparisonEventTotal()} leads
              </span>
            )}
            {getTotalLeads() > 0 && (
              <span style={{
                backgroundColor: '#fef3c7',
                color: '#92400e',
                fontWeight: 600,
                padding: '6px 12px',
                borderRadius: '16px',
                fontSize: '13px'
              }}>
                🏆 Top: {getTopPerformer()}
              </span>
            )}
          </div>
        </div>

        {/* Chart */}
        <div style={{ height: height - 120 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart 
              data={chartData} 
              margin={{ top: 5, right: 20, left: 10, bottom: 0 }}
            >
              <defs>
                <linearGradient id="currentEventGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#4ade80" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#4ade80" stopOpacity={0.1}/>
                </linearGradient>
                <linearGradient id="comparisonEventGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              
              <CartesianGrid 
                strokeDasharray="3 3" 
                vertical={false} 
                stroke="rgba(0,0,0,0.1)"
                strokeWidth={1}
              />
              
              <XAxis 
                dataKey="type" 
                axisLine={false} 
                tickLine={false}
                tick={{ 
                  fill: '#6b7280', 
                  fontSize: 13,
                  fontWeight: 500
                }}
                dy={5}
              />
              
              <YAxis 
                axisLine={false} 
                tickLine={false}
                tick={{ 
                  fill: '#6b7280', 
                  fontSize: 12,
                  fontWeight: 500
                }}
                dx={-10}
                domain={[0, 'dataMax + 5']}
                type="number"
                allowDecimals={false}
              />
              
              <Tooltip content={<CustomTooltip />} />
              
              <Area
                dataKey="currentEvent"
                name="Current Event"
                stroke="#16a34a"
                strokeWidth={3}
                fill="url(#currentEventGradient)"
                dot={{ 
                  r: 6, 
                  strokeWidth: 2, 
                  fill: '#fff',
                  stroke: '#16a34a'
                }}
                activeDot={{ 
                  r: 8, 
                  strokeWidth: 3,
                  fill: '#16a34a',
                  stroke: '#fff'
                }}
              />
              
              {comparisonEventId && (
                <Area
                  dataKey="comparisonEvent"
                  name="Comparison Event"
                  stroke="#7c3aed"
                  strokeWidth={3}
                  fill="url(#comparisonEventGradient)"
                  dot={{ 
                    r: 6, 
                    strokeWidth: 2, 
                    fill: '#fff',
                    stroke: '#7c3aed'
                  }}
                  activeDot={{ 
                    r: 8, 
                    strokeWidth: 3,
                    fill: '#7c3aed',
                    stroke: '#fff'
                  }}
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}